/**
 * screens/hooks/useGuestFlow.ts
 * Live guest self-join flow for the public Guest surface, backed by sk-queue-api
 * via the Backbone-minted guest token (guestSession). It mirrors the in-memory
 * store's guest API (step machine + party/phone/consent/otp + actions) so the
 * screen can swap sources with no JSX change:
 *
 *   join  → POST /queues/:id/entries   (consentGiven required for QR self-join)
 *   otp   → POST /entries/:id/verify   (JOINED → VERIFIED → WAITING)
 *   status→ poll GET /entries/:id       (live position/state via its events)
 *   remove→ POST /entries/:id/remove   (self-removal; 403 if disabled)
 *
 * Disabled (returns null) unless this is a public surface with a queue — the
 * screen then uses the in-memory store exactly as before.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { joinQueue, verifyEntry, removeEntry, getEntry } from '../../services/queue/queueService';
import { handleApiError } from '../../utils/errorHandler';
import type { GuestFlow, GuestStep } from '../../queue/types';
import type { EntryWithEvents } from '../../api/queueDtos';
import type { GuestQueueInfo } from '../../queue/guestSession';

const POLL_MS = 4000;

export interface GuestFlowApi {
  g: GuestFlow;
  /** The live entry (status step), polled — null until joined/verified. */
  entry: EntryWithEvents | null;
  guestSet: (patch: Partial<GuestFlow>) => void;
  guestParty: (delta: number) => void;
  guestName: (v: string) => void;
  guestPhone: (v: string) => void;
  guestToggleConsent: () => void;
  guestJoin: () => void;
  guestVerify: (code?: string) => void;
  guestRemove: () => void;
  guestReset: () => void;
}

const INITIAL: GuestFlow = { step: 'join', party: 2, name: '', phone: '', consent: false, otp: '', entryId: null };

/** A stable idempotency key per join attempt (no Date.now/random in module scope is fine here — runtime only). */
function makeIdemKey(): string {
  return `guest-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useGuestFlow(
  queueId: string | null,
  queueInfo: GuestQueueInfo | null,
  authHeader: () => Promise<Record<string, string> | null>,
): GuestFlowApi | null {
  const [g, setG] = useState<GuestFlow>(INITIAL);
  const [entry, setEntry] = useState<EntryWithEvents | null>(null);
  const idemRef = useRef<string>('');

  const guestSet = useCallback((patch: Partial<GuestFlow>) => setG((s) => ({ ...s, ...patch })), []);
  const guestParty = useCallback((delta: number) => {
    setG((s) => {
      const max = queueInfo?.maxSelfJoinParty ?? 12;
      return { ...s, party: Math.max(1, Math.min(max, s.party + delta)) };
    });
  }, [queueInfo?.maxSelfJoinParty]);
  const guestName = useCallback((v: string) => {
    setG((s) => ({ ...s, name: (v || '').slice(0, 160) }));
  }, []);
  const guestPhone = useCallback((v: string) => {
    // Exactly 10 digits, digits-only — same rule as the platform signup field.
    setG((s) => ({ ...s, phone: (v || '').replace(/\D/g, '').slice(0, 10) }));
  }, []);
  const guestToggleConsent = useCallback(() => setG((s) => ({ ...s, consent: !s.consent })), []);
  const guestReset = useCallback(() => { setEntry(null); setG(INITIAL); }, []);

  const guestJoin = useCallback(async () => {
    if (!queueId) return;
    const digits = g.phone.replace(/\D/g, '');
    // Belt-and-braces (the screen also validates): name required, phone exactly
    // 10 digits, consent ticked. Matches the backend's FITTED name+phone rule.
    if (!g.name.trim()) return;
    if (digits.length !== 10) return;
    if (!g.consent) return;
    try {
      const headers = await authHeader();
      if (!headers) return;
      idemRef.current = makeIdemKey();
      const { entry: e } = await joinQueue(
        queueId,
        {
          displayName: g.name.trim(),
          phoneE164: `+91${digits}`,
          partySize: g.party,
          joinChannel: 'QR',
          consentGiven: true,
          consentPurpose: 'waitlist',
          idempotencyKey: idemRef.current,
        },
        { headers },
      );
      // Self-join requires OTP verification next.
      setG((s) => ({ ...s, step: 'otp', otp: '', entryId: e.id }));
    } catch (err) {
      handleApiError(err, 'Could not join the waitlist');
    }
  }, [queueId, g.name, g.phone, g.consent, g.party, authHeader]);

  const guestVerify = useCallback(async (codeArg?: string) => {
    if (!g.entryId) return;
    // Prefer the code passed by the caller. onOtpChange schedules verify via
    // setTimeout right after guestSet({otp}); the closure's g.otp can still be
    // the pre-keystroke value (stale), so trusting it alone drops the submit.
    const code = (codeArg ?? g.otp).replace(/\D/g, '');
    if (code.length < 6) return;
    try {
      const headers = await authHeader();
      if (!headers) return;
      await verifyEntry(g.entryId, { code }, { headers });
      setG((s) => ({ ...s, step: 'status' }));
    } catch (err) {
      handleApiError(err, 'Could not verify the code');
    }
  }, [g.entryId, g.otp, authHeader]);

  const guestRemove = useCallback(async () => {
    if (!g.entryId) return;
    try {
      const headers = await authHeader();
      if (!headers) return;
      await removeEntry(g.entryId, { reasonCode: 'self-removal' }, { headers });
      setG((s) => ({ ...s, step: 'removed' }));
    } catch (err) {
      handleApiError(err, 'Could not remove you from the line');
    }
  }, [g.entryId, authHeader]);

  // Poll the entry while on the status step so position/state stay live.
  useEffect(() => {
    if (g.step !== 'status' || !g.entryId) { return; }
    let alive = true;
    const tick = async () => {
      try {
        const headers = await authHeader();
        if (!headers) return;
        const data = await getEntry(g.entryId as string, { headers });
        if (alive) setEntry(data);
      } catch {
        /* transient — keep the last good snapshot, next tick retries */
      }
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [g.step, g.entryId, authHeader]);

  if (!queueId) return null;

  return { g, entry, guestSet, guestParty, guestName, guestPhone, guestToggleConsent, guestJoin, guestVerify, guestRemove, guestReset };
}
